import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import { getCable } from '@/lib/cable'
import {
  loadViewerPayload,
  mergeViewerStreamPreview,
  removeViewerCamera,
  updateViewerDestinationLayout,
  updateViewerDestinationLiveState,
  updateViewerEventSettings,
  updateViewerFeaturedCamera,
  type ViewerPayload,
  type ViewerStreamPreview,
} from '@/lib/comrades/viewerPayload'

type ViewerEventSettings = {
  live_only?: boolean | null
}

type FeaturedCameraUpdateMessage = {
  type: 'featured_camera_update'
  featured_participant_identity?: string | null
  payload?: { featured_participant_identity?: string | null }
}

type DestinationLayoutPublishedMessage = {
  type: 'destination_layout_published'
  layout_revision?: number
  layout?: Record<string, unknown>
  live_state_revision?: number
  payload?: {
    layout_revision?: number
    layout?: Record<string, unknown>
    live_state_revision?: number
  }
}

type DestinationLiveStateUpdateMessage = {
  type: 'destination_live_state_update'
  payload?: {
    revision?: number
    component_instance_id?: string
    state?: Record<string, unknown> | null
    updated_at?: string | null
    expires_at?: string | null
  }
}

type EventPageMessage =
  | { type: 'stream_remove'; stream_id: string | number }
  | { type: 'stream_upsert'; stream: ViewerStreamPreview }
  | { type: 'event_settings_update'; event?: ViewerEventSettings; payload?: { event?: ViewerEventSettings } }
  | FeaturedCameraUpdateMessage
  | DestinationLayoutPublishedMessage
  | DestinationLiveStateUpdateMessage
  | { type: string; [key: string]: unknown }

type EventSettingsUpdateMessage = Extract<EventPageMessage, { type: 'event_settings_update' }>

function isFeaturedCameraUpdateMessage(
  message: EventPageMessage,
): message is FeaturedCameraUpdateMessage {
  return message.type === 'featured_camera_update'
}

export function usePublicEventViewerUpdates({
  eventId,
  payload,
  setPayload,
}: {
  eventId: string | null | undefined
  payload: ViewerPayload | null
  setPayload: Dispatch<SetStateAction<ViewerPayload | null>>
}) {
  const payloadRef = useRef(payload)
  const reloadInFlightRef = useRef(false)

  useEffect(() => {
    payloadRef.current = payload
  }, [payload])

  const applyPayload = useCallback((nextPayload: ViewerPayload) => {
    payloadRef.current = nextPayload
    setPayload(nextPayload)
  }, [setPayload])

  const reloadPayload = useCallback(() => {
    if (!eventId || reloadInFlightRef.current) return

    reloadInFlightRef.current = true
    loadViewerPayload(eventId)
      .then(applyPayload)
      .catch((error: unknown) => {
        console.warn('[public-viewer] failed to refresh viewer payload', error)
      })
      .finally(() => {
        reloadInFlightRef.current = false
      })
  }, [applyPayload, eventId])

  const handleMessage = useCallback((message: EventPageMessage) => {
    const current = payloadRef.current
    if (!current) return

    if (message.type === 'stream_remove' && message.stream_id) {
      applyPayload(removeViewerCamera(current, String(message.stream_id)))
      return
    }

    if (message.type === 'stream_upsert' && message.stream) {
      const result = mergeViewerStreamPreview(current, message.stream)
      if (result.known) {
        applyPayload(result.payload)
      } else {
        reloadPayload()
      }
      return
    }

    if (message.type === 'event_settings_update') {
      const settingsMessage = message as EventSettingsUpdateMessage
      const eventSettings = settingsMessage.event ?? settingsMessage.payload?.event
      if (typeof eventSettings?.live_only === 'boolean') {
        applyPayload(updateViewerEventSettings(current, { live_only: eventSettings.live_only }))
      } else {
        reloadPayload()
      }
      return
    }

    if (isFeaturedCameraUpdateMessage(message)) {
      const identity =
        message.featured_participant_identity ??
        message.payload?.featured_participant_identity ??
        null
      applyPayload(updateViewerFeaturedCamera(current, identity))
      return
    }

    if (message.type === 'destination_layout_published') {
      const destinationMessage = message as DestinationLayoutPublishedMessage
      const revision =
        destinationMessage.layout_revision ??
        destinationMessage.payload?.layout_revision
      if (typeof revision !== 'number') {
        reloadPayload()
        return
      }
      const result = updateViewerDestinationLayout(current, {
        layout_revision: revision,
        layout: destinationMessage.layout ?? destinationMessage.payload?.layout,
        live_state_revision:
          destinationMessage.live_state_revision ??
          destinationMessage.payload?.live_state_revision,
      })
      if (result.status === 'applied') applyPayload(result.payload)
      if (result.status === 'gap') reloadPayload()
      return
    }

    if (message.type === 'destination_live_state_update') {
      const update = (message as DestinationLiveStateUpdateMessage).payload
      if (
        typeof update?.revision !== 'number' ||
        typeof update.component_instance_id !== 'string'
      ) {
        reloadPayload()
        return
      }
      const result = updateViewerDestinationLiveState(current, {
        revision: update.revision,
        component_instance_id: update.component_instance_id,
        state: update.state ?? null,
        updated_at: update.updated_at ?? null,
        expires_at: update.expires_at ?? null,
      })
      if (result.status === 'applied') applyPayload(result.payload)
      if (result.status === 'gap') reloadPayload()
    }
  }, [applyPayload, reloadPayload])

  useEffect(() => {
    if (!eventId) return

    const cable = getCable()
    const unsubscribe = cable.subscribe(
      { channel: 'EventPageChannel', event_id: eventId },
      (message) => handleMessage(message as EventPageMessage),
    )

    return () => unsubscribe()
  }, [eventId, handleMessage])
}
